import { useState, useEffect } from "react";
import io from "socket.io-client";

const PLACEHOLDER_IMAGE =
  "https://resources.premierleague.com/premierleague25/photos/players/110x140/placeholder.png";

const primaryPhotoUrl = (code) =>
  `https://resources.premierleague.com/premierleague/photos/players/250x250/p${code}.png`;

const fallbackPhotoUrl = (code) =>
  `https://resources.premierleague.com/premierleague25/photos/players/110x140/${code}.png`;

// Try the 250x250 photo first, then the premierleague25 one, then a placeholder.
const handleImageError = (e) => {
  const img = e.target;
  const code = img.dataset.playerCode;
  if (img.src === primaryPhotoUrl(code)) {
    img.src = fallbackPhotoUrl(code);
  } else if (img.src !== PLACEHOLDER_IMAGE) {
    img.src = PLACEHOLDER_IMAGE;
  }
};

// Spread onto any <img> showing a player photo.
const playerPhotoProps = (player) => ({
  src: primaryPhotoUrl(player.code),
  "data-player-code": player.code,
  loading: "lazy",
  decoding: "async",
  onError: handleImageError,
});

const DraftApp = () => {
  const [socket, setSocket] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [gameState, setGameState] = useState({
    currentPick: 1,
    currentTeam: 5,
    teams: {
      1: { name: "FC Rast", picks: [] },
      2: { name: "FC Hagelschur", picks: [] },
      3: { name: "FC Rolf", picks: [] },
      4: { name: "FC Schröder", picks: [] },
      5: { name: "FC Jenson", picks: [] },
    },
    availablePlayers: [],
    draftedPlayers: [],
    isActive: false,
  });
  const [searchInput, setSearchInput] = useState("");
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [draftSummaryVisible, setDraftSummaryVisible] = useState(false);
  const [draftSummaryCurrentTeam, setDraftSummaryCurrentTeam] = useState(1);

  window.draftSummary = function (visible) {
    setDraftSummaryVisible(visible);
  };

  useEffect(() => {
    const socketConnection = io("https://jens-draft-backend.onrender.com", {
      secure: true,
    });
    setSocket(socketConnection);

    socketConnection.on("gameState", (newGameState) => {
      setGameState(newGameState);
    });

    return () => {
      socketConnection.disconnect();
    };
  }, []);

  useEffect(() => {
    handleSearchPlayer(searchInput, gameState.availablePlayers);
  }, [searchInput, gameState.availablePlayers]);

  const handleStartDraft = () => {
    if (socket) {
      socket.emit("startDraft");
    }
  };

  const handleResetDraft = () => {
    if (socket) {
      socket.emit("resetDraft");
    }
  };

  const handleDraftPlayer = (playerId) => {
    if (socket && gameState.isActive) {
      socket.emit("draftPlayer", playerId);
    }
  };

  const handleSearchPlayer = (search, playerList) => {
    const searchTerm = search.toLowerCase().trim();

    if (searchTerm) {
      const filtered = playerList.filter(
        (player) =>
          player.name.toLowerCase().includes(searchTerm) ||
          player.position.toLowerCase().includes(searchTerm) ||
          player.club.toLowerCase().includes(searchTerm),
      );
      setFilteredPlayers(filtered);
    } else {
      setFilteredPlayers(playerList);
    }
  };

  const getPicksWithPosition = (team, position) => {
    return team.picks.filter((player) => player.position === position);
  };

  const getTeamColor = (teamNumber) => {
    const colors = {
      1: "#228b22",
      2: "#00008b",
      3: "#008080",
      4: "#ff4500",
      5: "#ff1493",
    };
    return colors[teamNumber] || "#ccc";
  };

  const getTheme = () => ({
    // Light Mode
    light: {
      background: "#f5f5f5",
      cardBackground: "white",
      textPrimary: "#333",
      textSecondary: "#666",
      border: "#aaa",
      borderHover: "#007bff",
    },
    // Dark Mode
    dark: {
      background: "#1a1a1a",
      cardBackground: "#2d2d2d",
      textPrimary: "#ffffff",
      textSecondary: "#b0b0b0",
      border: "#404040",
      borderHover: "#4da6ff",
    },
  });

  const theme = getTheme()[darkMode ? "dark" : "light"];

  const styles = {
    container: {
      fontFamily: "Arial, sans-serif",
      margin: "0 auto",
      padding: "20px",
      backgroundColor: theme.background,
      minHeight: "100vh",
      transition: "all 0.3s ease",
    },
    draftSummary: {
      position: "fixed",
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      backgroundColor: theme.background,
      color: "white",
      padding: "40px",
      zIndex: 1000,
      overflow: "hidden",
      opacity: draftSummaryVisible ? 1 : 0,
      pointerEvents: draftSummaryVisible ? "auto" : "none",
      transition: "all 0.6s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
    },
    header: {
      textAlign: "center",
      marginBottom: "30px",
      marginRight: "460px",
      backgroundColor: theme.cardBackground,
      padding: "20px",
      borderRadius: "10px",
      boxShadow: darkMode
        ? "0 2px 10px rgba(0,0,0,0.5)"
        : "0 2px 10px rgba(0,0,0,0.1)",
      transition: "all 0.3s ease",
    },
    reisebueroImage: {
      width: "300px",
      height: "auto",
      marginBottom: "10px",
    },
    controls: {
      display: "flex",
      gap: "10px",
      justifyContent: "center",
      marginBottom: "20px",
      alignItems: "center",
    },
    button: {
      padding: "12px 24px",
      fontSize: "16px",
      border: "none",
      borderRadius: "5px",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },
    playersSearchButton: {
      backgroundColor: "#3498db",
      color: "white",
    },
    playersSearchInput: {
      padding: "10px",
      fontSize: "14px",
      borderRadius: "5px",
      border: darkMode ? "1px solid transparent" : `1px solid ${theme.border}`,
      width: "200px",
      transition: "all 0.3s ease",
      outline: "none",
      boxShadow: darkMode
        ? "0 2px 10px rgba(0,0,0,0.5)"
        : "0 2px 10px rgba(0,0,0,0.1)",
    },
    startButton: {
      backgroundColor: "#27ae60",
      color: "white",
    },
    resetButton: {
      backgroundColor: "#e74c3c",
      color: "white",
    },
    darkModeButton: {
      width: "48px",
      height: "48px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: darkMode ? "#f39c12" : "#34495e",
      color: "white",
      fontSize: "20px",
      borderRadius: "40px",
      position: "fixed",
      top: "10px",
      left: "10px",
      zIndex: 2000,
      boxShadow: darkMode
        ? "0 2px 10px rgba(0,0,0,0.5)"
        : "0 2px 10px rgba(0,0,0,0.1)",
      transition: "all 0.3s ease",
      userSelect: "none",
    },
    draftSummaryButton: {
      width: "48px",
      height: "48px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.cardBackground,
      color: "white",
      fontSize: "20px",
      borderRadius: "40px",
      position: "fixed",
      top: "10px",
      right: "10px",
      zIndex: 2000,
      boxShadow: darkMode
        ? "0 2px 10px rgba(0,0,0,0.5)"
        : "0 2px 10px rgba(0,0,0,0.1)",
      transition: "all 0.3s ease",
      userSelect: "none",
    },
    currentPick: {
      fontSize: "1.5em",
      fontWeight: "bold",
      color: theme.textPrimary,
      margin: "10px 0",
    },
    mainContent: {
      display: "grid",
      gridTemplateColumns: "1fr 400px",
      gap: "20px",
    },
    playersSection: {
      backgroundColor: theme.cardBackground,
      padding: "20px",
      marginRight: "40px",
      borderRadius: "10px",
      boxShadow: darkMode
        ? "0 2px 10px rgba(0,0,0,0.5)"
        : "0 2px 10px rgba(0,0,0,0.1)",
      transition: "all 0.3s ease",
    },
    playersHeader: {
      display: "flex",
      gap: "10px",
      borderBottom: `2px solid ${darkMode ? "#404040" : "#eee"}`,
      marginBottom: "20px",
    },
    playersSearchContainer: {
      display: "flex",
      alignItems: "center",
      marginLeft: "auto",
      gap: "10px",
    },
    sectionTitle: {
      fontSize: "1.8em",
      color: theme.textPrimary,
      marginBottom: "20px",

      paddingBottom: "10px",
    },
    playersGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
      gap: "15px",
    },
    playerCard: {
      border: `2px solid ${theme.border}`,
      borderRadius: "10px",
      padding: "15px",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.3s ease",
      backgroundColor: theme.cardBackground,
    },
    playerCardHover: {
      borderColor: theme.borderHover,
      transform: "translateY(-5px)",
      boxShadow: darkMode
        ? "0 5px 15px rgba(0,0,0,0.8)"
        : "0 5px 15px rgba(0,0,0,0.2)",
    },
    playerImage: {
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      objectFit: "cover",
      objectPosition: "top",
      marginBottom: "10px",
    },
    playerName: {
      fontSize: "14px",
      fontWeight: "bold",
      color: theme.textPrimary,
      marginBottom: "5px",
    },
    playerInfo: {
      fontSize: "12px",
      color: theme.textSecondary,
      marginBottom: "3px",
    },
    teamsSection: {
      position: "fixed",
      right: "20px",
      top: "20px",
      width: "400px",
      backgroundColor: theme.cardBackground,
      padding: "20px",
      borderRadius: "10px",
      boxShadow: darkMode
        ? "0 2px 10px rgba(0,0,0,0.5)"
        : "0 2px 10px rgba(0,0,0,0.1)",
      maxHeight: "calc(100vh - 80px)",
      overflowY: "auto",
      transition: "all 0.3s ease",
    },
    teamContainer: {
      marginBottom: "25px",
      padding: "15px",
      borderRadius: "8px",
      border: `2px solid ${theme.border}`,
      backgroundColor: darkMode ? "#333" : "#f9f9f9",
      transition: "all 0.3s ease",
    },
    teamHeader: {
      fontSize: "1.3em",
      fontWeight: "bold",
      marginBottom: "10px",
      padding: "8px",
      borderRadius: "5px",
      color: "white",
      textAlign: "center",
    },
    teamPicks: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
      gap: "8px",
    },
    teamPlayerCard: {
      padding: "8px",
      backgroundColor: darkMode ? "#404040" : "#f8f9fa",
      borderRadius: "5px",
      textAlign: "center",
      fontSize: "11px",
      transition: "all 0.3s ease",
    },
    teamPlayerName: {
      fontWeight: "bold",
      marginBottom: "3px",
      color: theme.textPrimary,
    },
    teamPlayerPosition: {
      color: theme.textSecondary,
      fontSize: "10px",
    },
    draftHistory: {
      backgroundColor: theme.cardBackground,
      padding: "20px",
      borderRadius: "10px",
      boxShadow: darkMode
        ? "0 2px 10px rgba(0,0,0,0.5)"
        : "0 2px 10px rgba(0,0,0,0.1)",
      marginTop: "20px",
      marginRight: "460px",
      maxHeight: "400px",
      overflowY: "auto",
      transition: "all 0.3s ease",
    },
    historyItem: {
      display: "flex",
      alignItems: "center",
      padding: "10px",
      borderBottom: `1px solid ${theme.border}`,
      gap: "15px",
    },
    pickNumber: {
      fontWeight: "bold",
      fontSize: "16px",
      minWidth: "40px",
      color: theme.textPrimary,
    },
    historyPlayerImage: {
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      objectFit: "cover",
      objectPosition: "top",
    },
    historyPlayerInfo: {
      flex: 1,
    },
    historyPlayerName: {
      fontWeight: "bold",
      marginBottom: "3px",
      color: theme.textPrimary,
    },
    historyTeam: {
      padding: "4px 8px",
      borderRadius: "12px",
      color: "white",
      fontSize: "12px",
      fontWeight: "bold",
    },
  };

  return (
    <main
      style={{
        height: "100vh",
        width: "100vw",
        overflow: draftSummaryVisible ? "hidden" : "auto",
      }}
    >
      <button
        style={{ ...styles.button, ...styles.darkModeButton }}
        onClick={() => setDarkMode(!darkMode)}
      >
        {darkMode ? "☀️" : "🌙"}
      </button>

      <button
        style={{ ...styles.button, ...styles.draftSummaryButton }}
        onClick={() => setDraftSummaryVisible(!draftSummaryVisible)}
      >
        {draftSummaryVisible ? "🔮" : "🌟"}
      </button>

      <div id="draft-summary-overlay" style={styles.draftSummary}>
        <h1
          style={{
            textAlign: "center",
            margin: "0",
            fontSize: "3em",
            color: darkMode ? "white" : "black",
          }}
        >
          {gameState.teams[draftSummaryCurrentTeam].name}
        </h1>

        <button
          style={{
            position: "absolute",
            top: "50%",
            left: "20px",
            transform: "translateY(-50%)",
            padding: "10px 20px",
            fontSize: "24px",
            fontWeight: "bold",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            backgroundColor: darkMode ? "#555" : "#eee",
            color: darkMode ? "white" : "black",
            boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
            userSelect: "none",
          }}
          onClick={() => {
            let nextTeam = draftSummaryCurrentTeam - 1;
            if (nextTeam < 1) {
              nextTeam = Object.keys(gameState.teams).length - 1;
            }
            setDraftSummaryCurrentTeam(nextTeam);
          }}
        >
          {"<"}
        </button>

        <button
          style={{
            position: "absolute",
            top: "50%",
            right: "20px",
            transform: "translateY(-50%)",
            padding: "10px 20px",
            fontSize: "24px",
            fontWeight: "bold",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            backgroundColor: darkMode ? "#555" : "#eee",
            color: darkMode ? "white" : "black",
            boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
            userSelect: "none",
          }}
          onClick={() => {
            let nextTeam = draftSummaryCurrentTeam + 1;
            if (nextTeam > Object.keys(gameState.teams).length) {
              nextTeam = 1;
            }
            setDraftSummaryCurrentTeam(nextTeam);
          }}
        >
          {">"}
        </button>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            backgroundImage: "url('field.png')",
            backgroundSize: "100% 100%",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            padding: "20px",
            height: "100%",
            width: "100%",
            userSelect: "none",
          }}
        >
          {[
            { position: "Forward", padding: "240px" },
            { position: "Midfielder", padding: "160px" },
            { position: "Defender", padding: "80px" },
            { position: "Goalkeeper", padding: "0px" },
          ].map((item) => (
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "space-evenly",
                alignItems: "center",
                boxSizing: "border-box",
                paddingLeft: item.padding,
                paddingRight: item.padding,
                minHeight: "200px",
              }}
            >
              {getPicksWithPosition(
                gameState.teams[draftSummaryCurrentTeam],
                item.position,
              ).map((player) => (
                <div
                  style={{
                    width: "200px",
                    textAlign: "center",
                    background:
                      "radial-gradient(ellipse farthest-corner at right bottom, #FEDB37 0%, #FDB931 8%, #9f7928 30%, #8A6E2F 40%, transparent 80%), radial-gradient(ellipse farthest-corner at left top, #FFFFFF 0%, #FFFFAC 8%, #D1B464 25%, #5d4a1f 62.5%, #5d4a1f 100%)",
                    padding: "20px",
                    borderRadius: "15px",
                    boxSizing: "border-box",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
                    zIndex: 1000,
                  }}
                >
                  <img
                    {...playerPhotoProps(player)}
                    alt={player.name}
                    style={{
                      height: "100%",
                      objectFit: "cover",
                      position: "absolute",
                      bottom: "0px",
                      left: "0px",
                      opacity: "0.3",
                      maskImage:
                        "linear-gradient(to right, rgba(0,0,0,1), rgba(0,0,0,0))",
                    }}
                    draggable="false"
                  />
                  <img
                    {...playerPhotoProps(player)}
                    alt={player.name}
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      objectPosition: "top",
                    }}
                    draggable="false"
                  />
                  <p style={{ margin: "0", fontWeight: "bold" }}>
                    {player.name}
                  </p>
                  <p style={{ margin: "0", fontSize: "12px" }}>
                    {player.position}
                  </p>
                  <p style={{ margin: "0", fontSize: "12px" }}>{player.club}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div style={styles.container} disabled={draftSummaryVisible}>
        <div style={styles.header}>
          <img
            src={"hagelschur.png"}
            alt="Hagelschur Logo"
            style={styles.reisebueroImage}
          />
          <div style={styles.controls}>
            <button
              style={{ ...styles.button, ...styles.startButton }}
              onClick={handleStartDraft}
              disabled={gameState.isActive}
            >
              Draft Starten
            </button>
            <button
              style={{ ...styles.button, ...styles.resetButton }}
              onClick={handleResetDraft}
            >
              Reset
            </button>
          </div>
          {gameState.isActive && (
            <div style={styles.currentPick}>
              Pick {gameState.currentPick}/55 -{" "}
              {gameState.teams[gameState.currentTeam].name} ist dran
            </div>
          )}
          {!gameState.isActive && gameState.currentPick > 55 && (
            <div style={{ ...styles.currentPick, color: "#27ae60" }}>
              Draft beendet! 🎉
            </div>
          )}
        </div>

        <div style={styles.mainContent}>
          <div style={styles.playersSection}>
            <div style={styles.playersHeader}>
              <h2 style={styles.sectionTitle}>
                Verfügbare Spieler ({gameState.availablePlayers.length})
              </h2>
              <div style={styles.playersSearchContainer}>
                <input
                  value={searchInput}
                  onInput={(e) => setSearchInput(e.target.value)}
                  style={styles.playersSearchInput}
                  placeholder="Suche"
                ></input>
              </div>
            </div>
            <div style={styles.playersGrid}>
              {filteredPlayers.map((player) => (
                <div
                  key={player.id}
                  className="player-card"
                  onClick={() => handleDraftPlayer(player.id)}
                >
                  <img
                    {...playerPhotoProps(player)}
                    alt={player.name}
                    style={styles.playerImage}
                  />
                  <div style={styles.playerName}>{player.name}</div>
                  <div style={styles.playerInfo}>{player.position}</div>
                  <div style={styles.playerInfo}>{player.club}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.teamsSection}>
            <h2 style={styles.sectionTitle}>Teams</h2>
            {Object.entries(gameState.teams).map(([teamNumber, team]) => (
              <div key={teamNumber} style={styles.teamContainer}>
                <div
                  style={{
                    ...styles.teamHeader,
                    backgroundColor: getTeamColor(parseInt(teamNumber)),
                  }}
                >
                  {team.name} ({team.picks.length}/11)
                </div>
                <div style={styles.teamPicks}>
                  {team.picks.map((player) => (
                    <div key={player.id} style={styles.teamPlayerCard}>
                      <div style={styles.teamPlayerName}>{player.name}</div>
                      <div style={styles.teamPlayerPosition}>
                        {player.position}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.draftHistory}>
          <h2 style={styles.sectionTitle}>Draft Verlauf</h2>
          {gameState.draftedPlayers
            .slice()
            .reverse()
            .map((player, index) => (
              <div
                key={`${player.id}-${player.pick}`}
                style={styles.historyItem}
              >
                <div style={styles.pickNumber}>#{player.pick}</div>
                <img
                  {...playerPhotoProps(player)}
                  alt={player.name}
                  style={styles.historyPlayerImage}
                />
                <div style={styles.historyPlayerInfo}>
                  <div style={styles.historyPlayerName}>{player.name}</div>
                  <div style={styles.playerInfo}>
                    {player.position} - {player.club}
                  </div>
                </div>
                <div
                  style={{
                    ...styles.historyTeam,
                    backgroundColor: getTeamColor(player.team),
                  }}
                >
                  {gameState.teams[player.team].name}
                </div>
              </div>
            ))}
        </div>
      </div>
    </main>
  );
};

export default DraftApp;
